import { useState, useEffect } from "react";

// @mui material components
import Card from "@mui/material/Card";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Radio from "@mui/material/Radio";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// core components
import PictureUpload from "components/CustomUpload/PictureUpload";

// DigiHapi components
import Activity from "components/DigiHapi/Activity";
import Notification from "components/DigiHapi/Notification";
import PhoneMaskInput from "components/DigiHapi/PhoneMaskInput";

// Redux
import { useDispatch, useSelector } from "react-redux";

import { fetchUpdateProfile } from "actions/profile";
import {
	SET_FIRST_NAME,
	SET_LAST_NAME,
	SET_GENDER,
	SET_PHONE,
	VALIDATE_PROFILE_INPUTS,
	CLEAR_PROFILE_VALIDATE,
	SET_PROFILE_FILES_IMAGES,
	REMOVE_PROFILE_IMAGE,
	SET_DESCRIPTION_FR,
	SET_DESCRIPTION_EN,
	SET_PROFILE_EDIT
} from "actions/types.js";

export default function ProfilePage(props) {
	const dispatch = useDispatch();

	const {
		isFetching,
		firstname,
		lastname,
		gender,
		address,
		zipCode,
		city,
		phone,
		description,
		firstnameErrorMsg,
		lastnameErrorMsg,
		phoneErrorMsg,
		isValid,
		clearValidate,
		photoUri,
		files,
		profileEdit,
		snackInfoMsg,
		snackErrorMsg,
		lang
	} = useSelector(state => state.profileReducer);

	useEffect(() => {
		if (clearValidate) {
			dispatch({
				type: VALIDATE_PROFILE_INPUTS
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clearValidate]);

	useEffect(() => {
		if (isValid) {
			dispatch(
				fetchUpdateProfile({
					photoUri,
					firstname,
					lastname,
					gender,
					address,
					zipCode,
					files,
					city,
					phone,
					description,
					lang
				})
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isValid]);

	const [br, setBr] = useState(false);
	const [message, setMessage] = useState(null);
	const [color, setColor] = useState("info");

	useEffect(() => {
		if (snackInfoMsg || snackErrorMsg) {
			setBr(true);
			setMessage(snackInfoMsg ? snackInfoMsg : snackErrorMsg);
			setColor(snackInfoMsg ? "info" : "danger");
			if (snackInfoMsg) {
				setTimeout(() => setBr(false), 5000);
			}
		}
	}, [snackInfoMsg, snackErrorMsg]);

	const updateProfile = e => {
		e.preventDefault();
		dispatch({
			type: CLEAR_PROFILE_VALIDATE
		});
	};

	const onImageLoaded = (name, imagePreviewUrl) => {
		dispatch({
			type: SET_PROFILE_FILES_IMAGES,
			payload: [{ name, base64: imagePreviewUrl }]
		});
	};

	const onImageRemoved = () => {
		dispatch({
			type: REMOVE_PROFILE_IMAGE
		});
	};

	const disabled = !profileEdit || isFetching;

	return (
		<Grid container spacing={3} justifyContent="center">
			<Grid item xs={12}>
				<Card>
					<MDBox
						mx={2}
						mt={-3}
						py={2}
						px={2}
						variant="gradient"
						bgColor="primary"
						borderRadius="lg"
						coloredShadow="primary"
						display="flex"
						alignItems="center"
						color="white"
					>
						<Icon>person</Icon>
						<MDTypography variant="h6" color="white" ml={1.5}>
							Profil
						</MDTypography>
					</MDBox>
					<MDBox p={2}>
						<Grid container spacing={3}>
							<Grid item xs={12} sm={3}>
								<PictureUpload
									onImageLoaded={onImageLoaded}
									onImageRemoved={onImageRemoved}
									disabled={disabled}
									photoUri={photoUri}
									gender={gender}
								/>
							</Grid>
							<Grid item xs={12} sm={9}>
								<Grid container spacing={3}>
									<Grid item xs={12} sm={4}>
										<FormControlLabel
											control={
												<Radio
													checked={!gender}
													onChange={e =>
														dispatch({
															type: SET_GENDER,
															value: null
														})
													}
													value=""
													name="unknown"
													disabled={disabled}
												/>
											}
											label="?"
										/>
										<FormControlLabel
											control={
												<Radio
													checked={gender === "M"}
													onChange={e =>
														dispatch({
															type: SET_GENDER,
															value: e.target.value
														})
													}
													value="M"
													name="mister"
													disabled={disabled}
												/>
											}
											label="M."
										/>
										<FormControlLabel
											control={
												<Radio
													checked={gender === "F"}
													onChange={e =>
														dispatch({
															type: SET_GENDER,
															value: e.target.value
														})
													}
													value="F"
													name="miss"
													disabled={disabled}
												/>
											}
											label="Mme"
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<MDInput
											label="Prénom *"
											id="firstname"
											fullWidth
											error={!!firstnameErrorMsg}
											helperText={firstnameErrorMsg}
											value={firstname || ""}
											onChange={e =>
												dispatch({
													type: SET_FIRST_NAME,
													value: e.target.value
												})
											}
											disabled={disabled}
										/>
									</Grid>
									<Grid item xs={12} sm={4}>
										<MDInput
											label="Nom *"
											id="lastname"
											fullWidth
											error={!!lastnameErrorMsg}
											helperText={lastnameErrorMsg}
											value={lastname || ""}
											onChange={e =>
												dispatch({
													type: SET_LAST_NAME,
													value: e.target.value
												})
											}
											disabled={disabled}
										/>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={12} sm={3}>
								<MDInput
									label="Téléphone"
									id="phone"
									fullWidth
									error={!!phoneErrorMsg}
									helperText={phoneErrorMsg}
									placeholder="+33 (0)X XX XX XX XX"
									InputProps={{
										inputComponent: profileEdit ? PhoneMaskInput : undefined
									}}
									value={phone || ""}
									onChange={e =>
										dispatch({
											type: SET_PHONE,
											value: e.target.value
										})
									}
									disabled={disabled}
								/>
							</Grid>
							<Grid item xs={12} sm={9}>
								<Grid container spacing={3}>
									<Grid item xs={12} sm={6}>
										<MDInput
											label="Description fr"
											id="description"
											fullWidth
											value={description || ""}
											onChange={e =>
												dispatch({
													type: SET_DESCRIPTION_FR,
													value: e.target.value
												})
											}
											disabled={disabled}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<MDInput
											label="Description en"
											id="description-en"
											fullWidth
											value={lang?.en?.description || ""}
											onChange={e =>
												dispatch({
													type: SET_DESCRIPTION_EN,
													value: e.target.value
												})
											}
											disabled={disabled}
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</MDBox>
					<MDBox p={2} display="flex" justifyContent="flex-end" gap={1}>
						<MDButton
							variant={profileEdit ? "outlined" : "gradient"}
							color={profileEdit ? "secondary" : "info"}
							onClick={() =>
								dispatch({ type: SET_PROFILE_EDIT, value: !profileEdit })
							}
						>
							{profileEdit ? "Annuler" : "Edition"}
						</MDButton>
						<MDButton
							variant="gradient"
							color={profileEdit ? "primary" : "secondary"}
							onClick={updateProfile}
							disabled={isFetching || !profileEdit}
						>
							{isFetching ? <Activity /> : "Modifier"}
						</MDButton>
					</MDBox>
				</Card>
			</Grid>
			<Notification
				place="br"
				color={color}
				message={message}
				open={br}
				onClose={() => setBr(false)}
			/>
		</Grid>
	);
}
